import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, ArrowRight, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import PageShell from './PageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KompassAssessment = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = done
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/kompass/questions`),
      axios.get(`${API}/user/kompass/${sessionId}`).catch(() => ({ data: { answers: {} } }))
    ]).then(([qRes, aRes]) => {
      setData(qRes.data);
      setAnswers(aRes.data?.answers || {});
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [sessionId]);

  const questions = useMemo(() => data?.questions || [], [data]);
  const dimensions = data?.dimensions || {};
  const totalSteps = questions.length + 1; // intro + N
  const currentQuestion = step > 0 ? questions[step - 1] : null;

  const setAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const persist = async (override = null) => {
    try {
      await axios.post(`${API}/user/kompass`, {
        session_id: sessionId,
        answers: override || answers
      });
    } catch (e) { console.error(e); }
  };

  const goNext = async () => {
    await persist();
    if (step >= totalSteps) {
      navigate(`/dashboard/${sessionId}`);
      return;
    }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    if (step === 0) {
      navigate(`/dashboard/${sessionId}`);
      return;
    }
    setStep(s => s - 1);
    window.scrollTo(0, 0);
  };

  const handleFinish = async () => {
    setSubmitting(true);
    await persist();
    setSubmitting(false);
    navigate(`/dashboard/${sessionId}`);
  };

  const answeredCount = useMemo(
    () => questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length,
    [questions, answers]
  );

  const canProceed = currentQuestion
    ? (currentQuestion.type === 'slider'
        ? true // slider has a meaningful default (50 = ausgeglichen)
        : answers[currentQuestion.id] !== undefined && answers[currentQuestion.id] !== '')
    : true;

  // Initialise slider answer to 50 the first time we land on a slider question
  useEffect(() => {
    if (currentQuestion && currentQuestion.type === 'slider' && answers[currentQuestion.id] === undefined) {
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: 50 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (loading) {
    return (
      <PageShell>
        <div className="py-20 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>Lade Kompass…</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Kompass"
      subtitle="Werte · Risiko · Autorität · Eigenverantwortung"
      icon={Compass}
      onClose={() => navigate(`/dashboard/${sessionId}`)}
      badge={step === 0 ? 'Intro' : step > questions.length ? 'Fertig' : `Frage ${step} / ${questions.length}`}
      maxWidth="max-w-2xl"
      testId="kompass-page"
    >
      {/* Progress bar */}
      <div className="mb-6 -mt-1">
        <div className="h-1" style={{ background: 'var(--border-default)', borderRadius: 9999, overflow: 'hidden' }}>
          <motion.div animate={{ width: `${(step / totalSteps) * 100}%` }} transition={{ duration: 0.4 }}
            className="h-full" style={{ background: 'var(--accent-cyan)', borderRadius: 9999 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <div className="p-5 mb-5" style={{
              background: 'linear-gradient(135deg, rgba(91,184,196,0.12) 0%, rgba(91,184,196,0.02) 100%)',
              border: '1px solid rgba(91,184,196,0.3)', borderRadius: '18px',
            }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
                BLOCK 5 · KOMPASS
              </p>
              <p className="text-3xl font-black mb-2" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                Dein innerer Navigator
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                12 kurze Fragen, vier Dimensionen. Es gibt keine richtige Antwort — nur deine ehrliche, im Moment. Beobachte dich selbst, ohne dich zu bewerten. Dieses Profil schärft dein Dossier und macht die Empfehlungen passgenau.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {Object.entries(dimensions).map(([id, dim]) => (
                <div key={id} className="p-4" style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px',
                }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: dim.color, letterSpacing: '0.08em' }}>
                    {dim.label.toUpperCase()}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {dim.intro}
                  </p>
                </div>
              ))}
            </div>

            <button onClick={goNext} className="page-shell-btn w-full" data-testid="kompass-start">
              Kompass kalibrieren <ArrowRight size={16} weight="bold" />
            </button>
          </motion.div>
        )}

        {currentQuestion && step > 0 && step <= questions.length && (
          <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}>
            {(() => {
              const dim = dimensions[currentQuestion.dimension] || {};
              const val = answers[currentQuestion.id];
              return (
                <div className="p-5 mb-5" style={{
                  background: `${dim.color}0A`,
                  border: `1px solid ${dim.color}30`,
                  borderRadius: '18px',
                }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: dim.color, letterSpacing: '0.1em' }}>
                    {dim.label?.toUpperCase()}
                  </p>
                  <p className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)', lineHeight: 1.45 }}>
                    {currentQuestion.prompt}
                  </p>
                  <p className="text-xs mb-5" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    {currentQuestion.hint}
                  </p>

                  {currentQuestion.type === 'slider' && (
                    <SliderInput
                      value={typeof val === 'number' ? val : 50}
                      onChange={(v) => setAnswer(currentQuestion.id, v)}
                      leftLabel={currentQuestion.left}
                      rightLabel={currentQuestion.right}
                      color={dim.color}
                      hasAnswered={val !== undefined && val !== ''}
                      testId={`kompass-slider-${currentQuestion.id}`}
                    />
                  )}

                  {currentQuestion.type === 'choice' && (
                    <div className="space-y-2.5">
                      {currentQuestion.options.map((opt) => {
                        const selected = val === opt.value;
                        return (
                          <button key={opt.value}
                            onClick={() => setAnswer(currentQuestion.id, opt.value)}
                            className="w-full text-left p-3.5 transition-all"
                            style={{
                              background: selected ? `${dim.color}1A` : 'var(--bg-surface)',
                              border: `1.5px solid ${selected ? dim.color : 'var(--border-default)'}`,
                              borderRadius: '12px', cursor: 'pointer',
                            }}
                            data-testid={`kompass-option-${currentQuestion.id}-${opt.value}`}>
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5"
                                style={{
                                  width: 18, height: 18, borderRadius: 9999,
                                  border: `2px solid ${selected ? dim.color : 'var(--border-default)'}`,
                                  background: selected ? dim.color : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                {selected && (
                                  <div style={{ width: 6, height: 6, borderRadius: 9999, background: '#fff' }} />
                                )}
                              </div>
                              <p className="text-sm" style={{
                                color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                                fontWeight: selected ? 600 : 500, lineHeight: 1.45
                              }}>
                                {opt.label}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
              <button onClick={goBack}
                className="order-2 sm:order-none flex items-center gap-2 px-4 py-2.5 text-xs font-semibold"
                style={{ background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '10px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                data-testid="kompass-back">
                <ArrowLeft size={14} /> Zurück
              </button>
              <p className="order-1 w-full text-center sm:order-none sm:w-auto text-xs" style={{ color: 'var(--text-secondary)' }}>
                {answeredCount} / {questions.length} beantwortet
              </p>
              {step < questions.length ? (
                <button onClick={goNext} disabled={!canProceed}
                  className="order-3 sm:order-none flex items-center gap-2 px-6 py-2.5 text-xs font-semibold"
                  style={{
                    background: canProceed ? 'var(--accent-cyan)' : 'var(--border-default)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    cursor: canProceed ? 'pointer' : 'not-allowed',
                    boxShadow: canProceed ? '0 4px 14px rgba(91,184,196,0.3)' : 'none',
                    opacity: canProceed ? 1 : 0.5,
                  }}
                  data-testid="kompass-next">
                  Weiter <ArrowRight size={14} weight="bold" />
                </button>
              ) : (
                <button onClick={() => { persist(); setStep(s => s + 1); window.scrollTo(0, 0); }}
                  disabled={!canProceed}
                  className="order-3 sm:order-none flex items-center gap-2 px-6 py-2.5 text-xs font-semibold"
                  style={{
                    background: canProceed ? 'var(--accent-cyan)' : 'var(--border-default)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    cursor: canProceed ? 'pointer' : 'not-allowed',
                    boxShadow: canProceed ? '0 4px 14px rgba(91,184,196,0.3)' : 'none',
                    opacity: canProceed ? 1 : 0.5,
                  }}
                  data-testid="kompass-to-finish">
                  Abschließen <ArrowRight size={14} weight="bold" />
                </button>
              )}
            </div>
          </motion.div>
        )}

        {step > questions.length && (
          <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <div className="p-6 mb-5 text-center" style={{
              background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(34,197,94,0.02) 100%)',
              border: '1px solid rgba(34,197,94,0.3)', borderRadius: '18px',
            }}>
              <CheckCircle size={48} weight="duotone" style={{ color: '#16A34A', margin: '0 auto 12px' }} />
              <p className="text-2xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
                Kompass kalibriert.
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Du hast dir 12 ehrliche Antworten gegönnt. Diese Daten geben deinem Dossier die Tiefe, die generische Tests nicht erreichen — Werte, Risiko-Profil, Hierarchie-Stil und Eigenverantwortung fließen jetzt in deine Strategische Projektion ein.
              </p>
            </div>
            <button onClick={handleFinish} disabled={submitting}
              className="page-shell-btn w-full"
              data-testid="kompass-finish">
              {submitting ? 'Speichert…' : 'Zurück zum Dashboard'} <ArrowRight size={16} weight="bold" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
};

const SliderInput = ({ value, onChange, leftLabel, rightLabel, color, hasAnswered, testId }) => {
  const pct = ((value - 0) / 100) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
        <span style={{ maxWidth: '45%', lineHeight: 1.3 }}>{leftLabel}</span>
        <span style={{ maxWidth: '45%', textAlign: 'right', lineHeight: 1.3 }}>{rightLabel}</span>
      </div>
      <input type="range" min="0" max="100" step="1" value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full h-2 cursor-pointer appearance-none rounded-full"
        style={{
          background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, var(--border-default) ${pct}%, var(--border-default) 100%)`,
          borderRadius: '9999px', outline: 'none', WebkitAppearance: 'none',
        }}
        data-testid={testId} />
      <div className="flex items-center justify-center mt-3">
        <div className="px-3 py-1 text-xs font-semibold"
          style={{
            background: hasAnswered ? `${color}20` : 'var(--bg-surface)',
            border: `1px solid ${hasAnswered ? color : 'var(--border-default)'}`,
            color: hasAnswered ? color : 'var(--text-secondary)',
            borderRadius: 9999,
          }}>
          {value} / 100
        </div>
      </div>
    </div>
  );
};

export default KompassAssessment;
